import { Component, OnInit, OnDestroy, HostListener, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, FormArray, Validators, ValidatorFn, AbstractControl, ValidationErrors } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import { forkJoin, of } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { ExpenseService } from '../../services/expense.service';
import { OrderService } from '../../services/order.service';
import { NgxSpinnerService } from 'ngx-spinner';
import { NotificationService } from '../../services/notification.service';
import { PermissionService } from '../../services/permission.service';
import {
    Expense,
    FuelExpense,
    TollExpense,
    AccommodationExpense,
    TrafficFineExpense,
    FoodExpense,
    OtherExpense,
    PaymentMethod,
    TollBooth,
    Route,
    Segment,
    FineType,
    ExpenseReport,
    SharedExpenseGroup
} from '../../interfaces/expense.interface';
import { UnifyViaticosModalComponent } from '../unify-viaticos-modal/unify-viaticos-modal.component';
import { UnifiedViaticosModalComponent } from '../unified-viaticos-modal/unified-viaticos-modal.component';
import { SeparationResultModalComponent } from '../separation-result-modal/separation-result-modal.component';

import Tesseract from 'tesseract.js';

@Component({
    selector: 'app-expense-report-form',
    templateUrl: './expense-report-form.component.html',
    styleUrls: ['./expense-report-form.component.scss']
})
export class ExpenseReportFormComponent implements OnInit, OnDestroy {
    @ViewChild('unifyModal') unifyModal!: UnifyViaticosModalComponent;
    @ViewChild('unifiedModal') unifiedModal!: UnifiedViaticosModalComponent;
    @ViewChild('separationModal') separationModal!: SeparationResultModalComponent;

    expenseForm: FormGroup;
    showSummary = false;
    isLoading = false;
    text_loading = 'Cargando...';
    submitButtonText = 'Registrar gastos de viáticos';
    getService: any;
    submitted = false;
    private loadedDeposits: any[] | null = null;

    sharedGroup: SharedExpenseGroup | null = null;
    activeTab: 'registro' | 'historial' = 'registro';
    serviceIdFromRoute = 0;
    fromServices = false;
    returnTab: string | null = null;
    registro1Collapsed = true;
    registro2Collapsed = false;
    preUnificationExpense: any = null;
    unifiedExpense: any = null;
    currentServiceExpenseId: number | null = null;

    get isUnified(): boolean {
        return !!this.sharedGroup;
    }

    // Status IDs considered terminal (finalizado, cancelado, etc.)
    private readonly TERMINAL_STATUS_IDS = [5, 6, 7];

    private get currentServiceIsTerminal(): boolean {
        return this.TERMINAL_STATUS_IDS.includes(this.getService?.status_id ?? 0);
    }

    // Registro #1 (individual): desbloqueado si el servicio actual es terminal,
    // o si quedan menos de 2 miembros activos en el grupo.
    get isIndividualFormLocked(): boolean {
        if (!this.sharedGroup) return false;
        if (this.currentServiceIsTerminal) return false;
        const activeCount = this.sharedGroup.services.filter(s =>
            !this.TERMINAL_STATUS_IDS.includes(s.status_id ?? 0)
        ).length;
        return activeCount >= 2;
    }

    // Registro #2 (unificado): bloqueado si el servicio actual ya es terminal,
    // o si quedan menos de 2 miembros activos.
    get isUnifiedFormLocked(): boolean {
        if (!this.sharedGroup) return false;
        if (this.currentServiceIsTerminal) return true;
        const activeCount = this.sharedGroup.services.filter(s =>
            !this.TERMINAL_STATUS_IDS.includes(s.status_id ?? 0)
        ).length;
        return activeCount < 2;
    }

    isOtherServiceItem(group: AbstractControl): boolean {
        const expId = group.get('expense_id')?.value;
        return !!expId && !!this.currentServiceExpenseId && +expId !== this.currentServiceExpenseId;
    }

    private hasAnyGroupExpenses(data: any): boolean {
        return ['fuel_expenses', 'toll_partida_expenses', 'toll_retorno_expenses',
                'accommodation_expenses', 'traffic_fine_expenses', 'food_expenses', 'other_expenses']
            .some(k => data[k]?.length > 0);
    }

    getServiceFolioById(serviceId: number): string {
        const svc = (this.sharedGroup as any)?.services?.find((s: any) => +s.service_id === +serviceId);
        return svc?.folio || '';
    }

    getPaymentMethodName(id: number | string): string {
        if (!id && id !== 0) return '';
        const pm = this.paymentMethods.find(p => +p.id === +id);
        return pm?.name || '';
    }

    expenseTypeLabel(type: string): string {
        const map: { [key: string]: string } = {
            fuel_expenses: 'Combustible',
            toll_partida_expenses: 'Caseta (partida)',
            toll_retorno_expenses: 'Caseta (retorno)',
            accommodation_expenses: 'Hospedaje',
            traffic_fine_expenses: 'Multa de tránsito',
            food_expenses: 'Alimentos',
            other_expenses: 'Otros gastos'
        };
        return map[type] || type;
    }

    formatAmount(amount: number): string {
        if (amount == null) return '';
        return new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN', minimumFractionDigits: 2 }).format(amount);
    }

    imageFile!: File;


    // Calculate total deposits dynamically
    get totalDeposits(): number {
        if (!this.getService?.deposits || this.getService.deposits.length === 0) {
            return 0;
        }
        return this.getService.deposits.reduce((total: number, deposit: any) => {
            return total + (parseFloat(deposit.amount) || 0);
        }, 0);
    }

    // Format currency
    formatCurrency(amount: number): string {
        return new Intl.NumberFormat('es-MX', {
            style: 'currency',
            currency: 'MXN',
            minimumFractionDigits: 2
        }).format(amount);
    }

    // Get deposit title dynamically
    getDepositTitle(index: number): string {
        if (index === 0) {
            return 'Deposito inicial';
        }
        return `Deposito #${index + 1}`;
    }

    get firstDepositAmount(): number {
        const deposits = this.getService?.deposits;
        if (!deposits?.length) return 0;
        return parseFloat(deposits[0].amount) || 0;
    }

    get lastDepositAmount(): number {
        const deposits = this.getService?.deposits;
        if (!deposits?.length) return 0;
        return parseFloat(deposits[deposits.length - 1].amount) || 0;
    }

    get depositCount(): number {
        return this.getService?.deposits?.length || 0;
    }

    // Calculate available balance (total deposits - total expenses)
    get availableBalance(): number {
        const totalDeposits = this.totalDeposits;
        const totalExpenses = this.calculateTotal();
        const result = totalDeposits - totalExpenses;
        return isNaN(result) ? 0 : result;
    }

    // Check if available balance is negative
    get hasNegativeBalance(): boolean {
        return this.availableBalance < 0;
    }

    // Navigate to deposit form
    goToDepositForm(): void {
        const serviceId = this.getService?.id;
        if (serviceId) {
            this.router.navigate(['/service/deposit', serviceId], { queryParams: { from: 'expense' } });
        }
    }
    imageSrc: any;

    text: string = '';
    jsonResult: any = {};

    // Dropdown options
    paymentMethods: PaymentMethod[] = [];
    tollBooths: TollBooth[] = [];
    routes: Route[] = [];
    segmentsByTollBooth: { [key: number]: Segment[] } = {};
    fineTypes: FineType[] = [];
    stayOptions = ['1 noche', '2 noches', '3 noches', '4+ noches'];
    foodDayOptions = ['Desayuno', 'Comida', 'Cena', 'Colación'];

    sectionCollapsed: { [key: string]: boolean } = {
        fuel: false,
        toll_partida: false,
        toll_retorno: false,
        accommodation: false,
        traffic_fine: false,
        food: false,
        other: false
    };

    fileNames: { [type: string]: { [index: number]: string } } = {};
    private uploadedPaths: { [type: string]: { [index: number]: string } } = {};
    private formSaved = false;

    getFileName(type: string, index: number): string {
        return this.fileNames[type]?.[index] || '';
    }

    getEvidenceUrl(type: string, index: number): string {
        const arrays: { [key: string]: any } = {
            fuel: this.fuelExpenses,
            toll_partida: this.tollPartidaExpenses,
            toll_retorno: this.tollRetornoExpenses,
            accommodation: this.accommodationExpenses,
            traffic_fine: this.trafficFineExpenses,
            food: this.foodExpenses,
            other: this.otherExpenses
        };
        return arrays[type]?.at(index)?.get('evidence')?.value || '#';
    }

    private setFileName(type: string, index: number, name: string): void {
        if (!this.fileNames[type]) this.fileNames[type] = {};
        this.fileNames[type][index] = name;
    }

    private setFileNameFromUrl(type: string, index: number, url: string): void {
        if (!url) return;
        const name = url.split('/').pop() || url;
        this.setFileName(type, index, name);
    }

    constructor(
        private fb: FormBuilder,
        private route: ActivatedRoute,
        public router: Router,
        private expenseService: ExpenseService,
        private orderService: OrderService,
        private spinner: NgxSpinnerService,
        private notificationService: NotificationService,
        public permissionService: PermissionService
    ) {
        this.expenseForm = this.createExpenseForm();
    }

    ngOnInit(): void {
        this.fromServices = this.route.snapshot.queryParamMap.get('from') === 'services';
        this.returnTab = this.route.snapshot.queryParamMap.get('tab');
        this.addInitialExpenses();
        this.loadAllData();
    }

    private loadAllData(): void {
        const serviceId = this.route.snapshot.paramMap.get('id');
        if (!serviceId) return;

        const id = parseInt(serviceId, 10);
        this.serviceIdFromRoute = id;
        this.spinner.show();
        this.expenseForm.patchValue({ service_id: id });

        forkJoin([
            this.expenseService.getPaymentMethods().pipe(catchError(() => of([]))),
            this.expenseService.getTollBooths().pipe(catchError(() => of([]))),
            this.expenseService.getFineTypes().pipe(catchError(() => of([]))),
            this.expenseService.getRoutes().pipe(catchError(() => of([]))),
            this.orderService.getId(id),
            this.expenseService.getExpenseByServiceId(id).pipe(catchError(() => of(null))),
            this.expenseService.getSharedGroupByService(id).pipe(catchError(() => of({ group_id: null })))
        ]).subscribe({
            next: ([paymentMethods, tollBooths, fineTypes, routes, service, expense, sharedGroupRes]: [any, any, any, any, any, any, any]) => {
                this.paymentMethods = paymentMethods;
                this.tollBooths = tollBooths;
                this.fineTypes = fineTypes;
                this.routes = routes;
                this.getService = service;
                this.sharedGroup = (sharedGroupRes?.group_id) ? sharedGroupRes as SharedExpenseGroup : null;
                this.currentServiceExpenseId = expense?.id ?? null;
                if (service?.status_id) {
                    this.expenseForm.patchValue({ status_id: service.status_id });
                }
                if (expense?.deposits) {
                    this.loadedDeposits = expense.deposits;
                    if (this.getService) {
                        this.getService.deposits = expense.deposits;
                    }
                }
                if (this.sharedGroup) {
                    this.submitButtonText = 'Guardar viáticos unificados';

                    // Registro #2: gastos unificados de TODOS los servicios del grupo (post joined_at)
                    // Registro #1: ítems del servicio actual que NO están en el grupo (pre joined_at)
                    // La separación se hace por IDs para evitar depender del formato de created_at
                    this.expenseService.getGroupUnifiedExpenses(this.sharedGroup.group_id).pipe(
                        catchError(() => of(null))
                    ).subscribe((groupExpenses: any) => {
                        const isMine = (i: any) => !i.expense_id || +i.expense_id === this.currentServiceExpenseId;
                        const myUnifiedIds = new Set<number>();

                        // Filtrar por service_ids: un ítem solo se muestra al servicio actual
                        // si su service_ids lo incluye. Ítems sin service_ids se incluyen siempre
                        // (datos legados anteriores al campo).
                        const forThisService = (items: any[]) =>
                            (items || []).filter((i: any) => {
                                if (!Array.isArray(i.service_ids) || i.service_ids.length === 0) return true;
                                return i.service_ids.map((id: any) => +id).includes(+this.serviceIdFromRoute);
                            });

                        const allGroupExpenses = {
                            id: expense?.id ?? null,
                            service_id: expense?.service_id ?? this.serviceIdFromRoute,
                            status_id: expense?.status_id ?? null,
                            fuel_expenses:          forThisService(groupExpenses?.fuel_expenses),
                            toll_partida_expenses:  forThisService(groupExpenses?.toll_partida_expenses),
                            toll_retorno_expenses:  forThisService(groupExpenses?.toll_retorno_expenses),
                            accommodation_expenses: forThisService(groupExpenses?.accommodation_expenses),
                            traffic_fine_expenses:  forThisService(groupExpenses?.traffic_fine_expenses),
                            food_expenses:          forThisService(groupExpenses?.food_expenses),
                            other_expenses:         forThisService(groupExpenses?.other_expenses),
                        };

                        this.unifiedExpense = this.hasAnyGroupExpenses(allGroupExpenses)
                            ? allGroupExpenses : null;

                        // myUnifiedIds usa solo ítems propios para computar Registro #1
                        ['fuel_expenses','toll_partida_expenses','toll_retorno_expenses',
                         'accommodation_expenses','traffic_fine_expenses','food_expenses','other_expenses']
                            .forEach(key => {
                                (groupExpenses?.[key] || [])
                                    .filter(isMine)
                                    .filter((i: any) => i.id)
                                    .forEach((i: any) => myUnifiedIds.add(+i.id));
                            });

                        // Registro #1 = ítems del servicio actual que no están en Registro #2.
                        // La separación se basa en es_individual/grupo_id (campos explícitos en BD),
                        // por lo que el filtro de fecha joined_at ya no es necesario ni correcto.
                        if (expense?.id) {
                            const notUnified = (items: any[]) =>
                                (items || []).filter((i: any) => {
                                    if (!i.id) return false;
                                    if (myUnifiedIds.has(+i.id)) return false;
                                    return true;
                                });
                            const preItems = {
                                id: expense.id,
                                service_id: expense.service_id,
                                status_id: expense.status_id,
                                fuel_expenses:          notUnified(expense.fuel_expenses || []),
                                toll_partida_expenses:  notUnified(expense.toll_partida_expenses?.length ? expense.toll_partida_expenses : (expense.toll_expenses || [])),
                                toll_retorno_expenses:  notUnified(expense.toll_retorno_expenses || []),
                                accommodation_expenses: notUnified(expense.accommodation_expenses || []),
                                traffic_fine_expenses:  notUnified(expense.traffic_fine_expenses || []),
                                food_expenses:          notUnified(expense.food_expenses || []),
                                other_expenses:         notUnified(expense.other_expenses || []),
                            };
                            const preCount = ['fuel_expenses','toll_partida_expenses','toll_retorno_expenses',
                                'accommodation_expenses','traffic_fine_expenses','food_expenses','other_expenses']
                                .reduce((sum, k) => sum + ((preItems as any)[k]?.length ?? 0), 0);
                            this.preUnificationExpense = preCount > 0 ? preItems : null;
                        } else {
                            this.preUnificationExpense = null;
                        }

                        // Precargar segmentos para todas las casetas del grupo
                        const allTolls = [
                            ...(allGroupExpenses?.toll_partida_expenses || []),
                            ...(allGroupExpenses?.toll_retorno_expenses || []),
                        ];
                        const uniqueBoothIdsG = [...new Set(
                            allTolls.map((t: any) => t.toll_booth_id).filter(Boolean)
                        )] as number[];

                        const afterSegmentsG = () => {
                            if (this.isUnifiedFormLocked) {
                                this.sharedGroup!.services
                                    .filter(s => this.TERMINAL_STATUS_IDS.includes(s.status_id ?? 0) && s.is_active)
                                    .forEach(s => {
                                        this.expenseService.leaveSharedGroup(this.sharedGroup!.group_id, s.service_id).subscribe();
                                    });
                                this.registro1Collapsed = false;
                                this.registro2Collapsed = true;
                                if (this.preUnificationExpense) {
                                    this.populateFormWithExpense(this.preUnificationExpense);
                                } else {
                                    this.clearFormArrays();
                                    this.addInitialExpenses();
                                }
                            } else if (this.unifiedExpense) {
                                this.populateFormWithExpense(this.unifiedExpense);
                            } else {
                                this.clearFormArrays();
                                this.addInitialExpenses();
                            }
                            this.spinner.hide();
                        };

                        if (uniqueBoothIdsG.length === 0) { afterSegmentsG(); return; }
                        forkJoin(
                            uniqueBoothIdsG.map(boothId =>
                                this.expenseService.getSegmentsByTollBooth(boothId).pipe(
                                    catchError(() => of([])),
                                    tap((segments: any) => { this.segmentsByTollBooth[boothId] = segments; })
                                )
                            )
                        ).subscribe({ next: () => afterSegmentsG(), error: () => afterSegmentsG() });
                    });
                    return;
                }
                if (!expense?.id) {
                    this.spinner.hide();
                    return;
                }
                const partidas: any[] = expense.toll_partida_expenses?.length
                    ? expense.toll_partida_expenses
                    : (expense.toll_expenses || []);
                const retornos: any[] = expense.toll_retorno_expenses || [];
                const uniqueBoothIds = [...new Set(
                    [...partidas, ...retornos]
                        .map((t: any) => t.toll_booth_id)
                        .filter(Boolean)
                )] as number[];

                if (uniqueBoothIds.length === 0) {
                    this.populateFormWithExpense(expense);
                    this.submitButtonText = 'Actualizar viáticos';
                    this.spinner.hide();
                    return;
                }

                forkJoin(
                    uniqueBoothIds.map(boothId =>
                        this.expenseService.getSegmentsByTollBooth(boothId).pipe(
                            catchError(() => of([])),
                            tap((segments: any) => { this.segmentsByTollBooth[boothId] = segments; })
                        )
                    )
                ).subscribe({
                    next: () => {
                        this.populateFormWithExpense(expense);
                        this.submitButtonText = 'Actualizar viáticos';
                        this.spinner.hide();
                    },
                    error: () => {
                        this.populateFormWithExpense(expense);
                        this.submitButtonText = 'Actualizar viáticos';
                        this.spinner.hide();
                    }
                });
            },
            error: () => this.spinner.hide()
        });
    }


    openRegistro1(): void {
        if (!this.registro1Collapsed) {
            this.registro1Collapsed = true;
            return;
        }
        this.registro1Collapsed = false;
        this.registro2Collapsed = true;
        if (this.preUnificationExpense) {
            this.populateFormWithExpense(this.preUnificationExpense);
        }
    }

    openRegistro2(): void {
        if (!this.registro2Collapsed) {
            this.registro2Collapsed = true;
            return;
        }
        this.registro2Collapsed = false;
        this.registro1Collapsed = true;
        if (this.unifiedExpense) {
            this.populateFormWithExpense(this.unifiedExpense);
        } else {
            this.clearFormArrays();
            this.addInitialExpenses();
        }
    }

    openUnifyModal(): void {
        if (this.unifyModal) {
            this.unifyModal.open();
        }
    }

    openUnifiedModal(): void {
        if (this.unifiedModal) {
            this.unifiedModal.open();
        }
    }

    onGroupCreated(group: SharedExpenseGroup): void {
        const fv = this.expenseForm.value;
        this.preUnificationExpense = {
            fuel_expenses: fv.fuel_expenses,
            toll_partida_expenses: fv.toll_partida_expenses,
            toll_retorno_expenses: fv.toll_retorno_expenses,
            accommodation_expenses: fv.accommodation_expenses,
            traffic_fine_expenses: fv.traffic_fine_expenses,
            food_expenses: fv.food_expenses,
            other_expenses: fv.other_expenses,
        };
        this.unifiedExpense = null;
        this.sharedGroup = group;
        this.registro1Collapsed = true;
        this.registro2Collapsed = false;
        this.clearFormArrays();
        this.addInitialExpenses();
        this.submitButtonText = 'Guardar viáticos unificados';
    }

    onGroupDissolved(): void {
        if (this.separationModal) {
            this.separationModal.open();
        }
    }

    onSeparationChoiceUnify(): void {
        this.sharedGroup = null;
        this.preUnificationExpense = null;
        this.unifiedExpense = null;
        if (this.unifyModal) {
            this.unifyModal.open();
        }
    }

    onSeparationChoiceAlone(): void {
        this.sharedGroup = null;
        this.preUnificationExpense = null;
        this.unifiedExpense = null;
        this.activeTab = 'registro';
        this.submitButtonText = 'Registrar gastos de viáticos';
        this.loadAllData();
    }

    populateFormWithExpense(expense: any): void {
        this.clearFormArrays();

        this.expenseForm.patchValue({
            service_id: expense.service_id,
            status_id: expense.status_id,
            comments: expense.comments || ''
        });

        const fill = <T>(items: T[] | null | undefined, add: (d: T, i: number) => void, empty: () => void) => {
            if (items?.length) { items.forEach(add); } else { empty(); }
        };

        fill(expense.fuel_expenses, (d: any, i: any) => {
            this.addFuelExpense(d);
            this.setFileNameFromUrl('fuel', i, d.evidence);
        }, () => this.addFuelExpense());

        // Las casetas registradas por TAG (source === 'tag') no son editables en este
        // formulario de casetas en efectivo: se excluyen para no cargarlas ni reenviarlas
        // como si fueran registros manuales.
        const cashOnly = (items: any[] | null | undefined) =>
            (items || []).filter((d: any) => d.source !== 'tag');

        const partidas = expense.toll_partida_expenses?.length
            ? expense.toll_partida_expenses : (expense.toll_expenses || null);
        fill(cashOnly(partidas), (d: any, i: any) => {
            this.addTollPartidaExpense(d);
            this.setFileNameFromUrl('toll_partida', i, d.evidence);
        }, () => this.addTollPartidaExpense());

        fill(cashOnly(expense.toll_retorno_expenses), (d: any, i: any) => {
            this.addTollRetornoExpense(d);
            this.setFileNameFromUrl('toll_retorno', i, d.evidence);
        }, () => this.addTollRetornoExpense());

        fill(expense.accommodation_expenses, (d: any, i: any) => {
            this.addAccommodationExpense(d);
            this.setFileNameFromUrl('accommodation', i, d.evidence);
        }, () => this.addAccommodationExpense());

        fill(expense.traffic_fine_expenses, (d: any, i: any) => {
            this.addTrafficFineExpense(d);
            this.setFileNameFromUrl('traffic_fine', i, d.evidence);
        }, () => this.addTrafficFineExpense());

        fill(expense.food_expenses, (d: any, i: any) => {
            this.addFoodExpense(d);
            this.setFileNameFromUrl('food', i, d.evidence);
        }, () => this.addFoodExpense());

        fill(expense.other_expenses, (d: any, i: any) => {
            this.addOtherExpense(d);
            this.setFileNameFromUrl('other', i, d.evidence);
        }, () => this.addOtherExpense());
    }

    clearFormArrays(): void {
        const fuelArray = this.expenseForm.get('fuel_expenses') as FormArray;
        const tollPartidaArray = this.expenseForm.get('toll_partida_expenses') as FormArray;
        const tollRetornoArray = this.expenseForm.get('toll_retorno_expenses') as FormArray;
        const accArray = this.expenseForm.get('accommodation_expenses') as FormArray;
        const fineArray = this.expenseForm.get('traffic_fine_expenses') as FormArray;
        const foodArray = this.expenseForm.get('food_expenses') as FormArray;
        const otherArray = this.expenseForm.get('other_expenses') as FormArray;

        while (fuelArray.length) fuelArray.removeAt(0);
        while (tollPartidaArray.length) tollPartidaArray.removeAt(0);
        while (tollRetornoArray.length) tollRetornoArray.removeAt(0);
        while (accArray.length) accArray.removeAt(0);
        while (fineArray.length) fineArray.removeAt(0);
        while (foodArray.length) foodArray.removeAt(0);
        while (otherArray.length) otherArray.removeAt(0);
    }

    addInitialExpenses(): void {
        this.addFuelExpense();
        this.addTollPartidaExpense();
        this.addTollRetornoExpense();
        this.addAccommodationExpense();
        this.addTrafficFineExpense();
        this.addFoodExpense();
        this.addOtherExpense();
    }

    createExpenseForm(): FormGroup {
        return this.fb.group({
            service_id: ['', Validators.required],
            status_id: [''],
            fuel_expenses: this.fb.array([]),
            toll_partida_expenses: this.fb.array([]),
            toll_retorno_expenses: this.fb.array([]),
            accommodation_expenses: this.fb.array([]),
            traffic_fine_expenses: this.fb.array([]),
            food_expenses: this.fb.array([]),
            other_expenses: this.fb.array([])
        });
    }

    // FormArray getters
    get fuelExpenses(): FormArray {
        return this.expenseForm.get('fuel_expenses') as FormArray;
    }

    get tollPartidaExpenses(): FormArray {
        return this.expenseForm.get('toll_partida_expenses') as FormArray;
    }

    get tollRetornoExpenses(): FormArray {
        return this.expenseForm.get('toll_retorno_expenses') as FormArray;
    }

    get accommodationExpenses(): FormArray {
        return this.expenseForm.get('accommodation_expenses') as FormArray;
    }

    get trafficFineExpenses(): FormArray {
        return this.expenseForm.get('traffic_fine_expenses') as FormArray;
    }

    get foodExpenses(): FormArray {
        return this.expenseForm.get('food_expenses') as FormArray;
    }

    get otherExpenses(): FormArray {
        return this.expenseForm.get('other_expenses') as FormArray;
    }

    // Custom validators for conditional validation
    private fuelFieldValidator(): ValidatorFn {
        return (control: AbstractControl): ValidationErrors | null => {
            const formGroup = control as FormGroup;
            const mileage = formGroup.get('mileage')?.value;
            const liters = formGroup.get('liters')?.value;
            const amount = formGroup.get('amount')?.value;
            const paymentMethodId = formGroup.get('payment_method_id')?.value;

            // Check if any field has a value
            const hasAnyValue = (mileage && mileage !== '') || 
                              (liters && liters !== '') || 
                              (amount && amount !== '') || 
                              (paymentMethodId && paymentMethodId !== '');

            if (!hasAnyValue) {
                return null; // No validation required if all fields are empty
            }

            const errors: ValidationErrors = {};

            // Validate each field if any field has a value
            if (!mileage || mileage === '') {
                errors['mileageRequired'] = true;
            } else if (isNaN(mileage) || parseFloat(mileage) < 0) {
                errors['mileageInvalid'] = true;
            }

            if (!liters || liters === '') {
                errors['litersRequired'] = true;
            } else if (isNaN(liters) || parseFloat(liters) < 0) {
                errors['litersInvalid'] = true;
            }

            if (!amount || amount === '') {
                errors['amountRequired'] = true;
            } else if (isNaN(amount) || parseFloat(amount) < 0) {
                errors['amountInvalid'] = true;
            }

            if (!paymentMethodId || paymentMethodId === '') {
                errors['paymentMethodRequired'] = true;
            }

            return Object.keys(errors).length > 0 ? errors : null;
        };
    }

    private tollFieldValidator(): ValidatorFn {
        return (control: AbstractControl): ValidationErrors | null => {
            const formGroup = control as FormGroup;
            const amount = formGroup.get('amount')?.value;
            const paymentMethodId = formGroup.get('payment_method_id')?.value;
            const tollBoothId = formGroup.get('toll_booth_id')?.value;

            const hasAnyValue = (amount && amount !== '') ||
                              (paymentMethodId && paymentMethodId !== '') ||
                              (tollBoothId && tollBoothId !== '');

            if (!hasAnyValue) {
                return null;
            }

            const errors: ValidationErrors = {};

            if (!amount || amount === '') {
                errors['amountRequired'] = true;
            } else if (isNaN(amount) || parseFloat(amount) < 0) {
                errors['amountInvalid'] = true;
            }

            if (!paymentMethodId || paymentMethodId === '') {
                errors['paymentMethodRequired'] = true;
            }

            if (!tollBoothId || tollBoothId === '') {
                errors['tollBoothRequired'] = true;
            }

            return Object.keys(errors).length > 0 ? errors : null;
        };
    }

    private accommodationFieldValidator(): ValidatorFn {
        return (control: AbstractControl): ValidationErrors | null => {
            const formGroup = control as FormGroup;
            const amount = formGroup.get('amount')?.value;
            const paymentMethodId = formGroup.get('payment_method_id')?.value;
            const stayDuration = formGroup.get('stay_duration')?.value;

            // Check if any field has a value
            const hasAnyValue = (amount && amount !== '') || 
                              (paymentMethodId && paymentMethodId !== '') || 
                              (stayDuration && stayDuration !== '');

            if (!hasAnyValue) {
                return null; // No validation required if all fields are empty
            }

            const errors: ValidationErrors = {};

            // Validate each field if any field has a value
            if (!amount || amount === '') {
                errors['amountRequired'] = true;
            } else if (isNaN(amount) || parseFloat(amount) < 0) {
                errors['amountInvalid'] = true;
            }

            if (!paymentMethodId || paymentMethodId === '') {
                errors['paymentMethodRequired'] = true;
            }

            if (!stayDuration || stayDuration === '') {
                errors['stayDurationRequired'] = true;
            }

            return Object.keys(errors).length > 0 ? errors : null;
        };
    }

    private trafficFineFieldValidator(): ValidatorFn {
        return (control: AbstractControl): ValidationErrors | null => {
            const formGroup = control as FormGroup;
            const amount = formGroup.get('amount')?.value;
            const paymentMethodId = formGroup.get('payment_method_id')?.value;
            const fineTypeId = formGroup.get('fine_type_id')?.value;
            const justification = formGroup.get('justification')?.value;

            // Check if any field has a value
            const hasAnyValue = (amount && amount !== '') || 
                              (paymentMethodId && paymentMethodId !== '') || 
                              (fineTypeId && fineTypeId !== '') || 
                              (justification && justification !== '');

            if (!hasAnyValue) {
                return null; // No validation required if all fields are empty
            }

            const errors: ValidationErrors = {};

            // Validate each field if any field has a value
            if (!amount || amount === '') {
                errors['amountRequired'] = true;
            } else if (isNaN(amount) || parseFloat(amount) < 0) {
                errors['amountInvalid'] = true;
            }

            if (!paymentMethodId || paymentMethodId === '') {
                errors['paymentMethodRequired'] = true;
            }

            if (!fineTypeId || fineTypeId === '') {
                errors['fineTypeRequired'] = true;
            }

            if (!justification || justification === '') {
                errors['justificationRequired'] = true;
            }

            return Object.keys(errors).length > 0 ? errors : null;
        };
    }

    private foodFieldValidator(): ValidatorFn {
        return (control: AbstractControl): ValidationErrors | null => {
            const formGroup = control as FormGroup;
            const amount = formGroup.get('amount')?.value;
            const foodDay = formGroup.get('description')?.value;
            const paymentMethodId = formGroup.get('payment_method_id')?.value;

            // Check if any field has a value
            const hasAnyValue = (amount && amount !== '') ||
                              (foodDay && foodDay !== '') ||
                              (paymentMethodId && paymentMethodId !== '');

            if (!hasAnyValue) {
                return null;
            }

            const errors: ValidationErrors = {};

            if (!amount || amount === '') {
                errors['amountRequired'] = true;
            } else if (isNaN(amount) || parseFloat(amount) < 0) {
                errors['amountInvalid'] = true;
            }

            if (!foodDay || foodDay === '') {
                errors['foodDayRequired'] = true;
            }

            if (!paymentMethodId || paymentMethodId === '') {
                errors['paymentMethodRequired'] = true;
            }

            return Object.keys(errors).length > 0 ? errors : null;
        };
    }

    private otherFieldValidator(): ValidatorFn {
        return (control: AbstractControl): ValidationErrors | null => {
            const formGroup = control as FormGroup;
            const amount = formGroup.get('amount')?.value;
            const description = formGroup.get('description')?.value;
            const paymentMethodId = formGroup.get('payment_method_id')?.value;

            // Check if any field has a value
            const hasAnyValue = (amount && amount !== '') || 
                              (description && description !== '') || 
                              (paymentMethodId && paymentMethodId !== '');

            if (!hasAnyValue) {
                return null; // No validation required if all fields are empty
            }

            const errors: ValidationErrors = {};

            // Validate each field if any field has a value
            if (!amount || amount === '') {
                errors['amountRequired'] = true;
            } else if (isNaN(amount) || parseFloat(amount) < 0) {
                errors['amountInvalid'] = true;
            }

            if (!description || description === '') {
                errors['descriptionRequired'] = true;
            }

            if (!paymentMethodId || paymentMethodId === '') {
                errors['paymentMethodRequired'] = true;
            }

            return Object.keys(errors).length > 0 ? errors : null;
        };
    }

    compareIds(a: any, b: any): boolean {
        if (a == null || a === '') return b == null || b === '';
        if (b == null || b === '') return false;
        return String(a) === String(b);
    }

    // Fuel expense methods
    addFuelExpense(data?: any): void {
        const fuelGroup = this.fb.group({
            id: [data?.id ?? null],
            expense_id: [data?.expense_id ?? null],
            service_id: [data?.service_id ?? null],
            mileage: [data?.mileage ?? ''],
            liters: [data?.liters ?? ''],
            amount: [data?.amount ?? ''],
            payment_method_id: [data?.payment_method_id != null ? +data.payment_method_id : ''],
            evidence: [data?.evidence ?? '']
        }, { validators: this.fuelFieldValidator() });
        this.fuelExpenses.push(fuelGroup);
    }

    private removeExpenseItem(arr: any, index: number, type: string): void {
        const ctrl = arr.at(index);
        const id = ctrl?.get('id')?.value;
        if (id && this.isOtherServiceItem(ctrl)) {
            this.expenseService.deleteSharedItem(type, +id).subscribe();
        }
        arr.removeAt(index);
    }

    removeFuelExpense(index: number): void {
        this.removeExpenseItem(this.fuelExpenses, index, 'fuel');
    }

    // Toll partida expense methods
    addTollPartidaExpense(data?: any): void {
        const tollGroup = this.fb.group({
            id: [data?.id ?? null],
            expense_id: [data?.expense_id ?? null],
            service_id: [data?.service_id ?? null],
            amount: [data?.amount ?? ''],
            payment_method_id: [data?.payment_method_id != null ? +data.payment_method_id : ''],
            toll_booth_id: [data?.toll_booth_id != null ? +data.toll_booth_id : ''],
            segment_id: [data?.segment_id != null ? +data.segment_id : ''],
            evidence: [data?.evidence ?? '']
        }, { validators: this.tollFieldValidator() });
        this.tollPartidaExpenses.push(tollGroup);
    }

    removeTollPartidaExpense(index: number): void {
        this.removeExpenseItem(this.tollPartidaExpenses, index, 'toll_partida');
    }

    // Toll retorno expense methods
    addTollRetornoExpense(data?: any): void {
        const tollGroup = this.fb.group({
            id: [data?.id ?? null],
            expense_id: [data?.expense_id ?? null],
            service_id: [data?.service_id ?? null],
            amount: [data?.amount ?? ''],
            payment_method_id: [data?.payment_method_id != null ? +data.payment_method_id : ''],
            toll_booth_id: [data?.toll_booth_id != null ? +data.toll_booth_id : ''],
            segment_id: [data?.segment_id != null ? +data.segment_id : ''],
            evidence: [data?.evidence ?? '']
        }, { validators: this.tollFieldValidator() });
        this.tollRetornoExpenses.push(tollGroup);
    }

    removeTollRetornoExpense(index: number): void {
        this.removeExpenseItem(this.tollRetornoExpenses, index, 'toll_retorno');
    }

    // Method to handle toll booth change and load segments
    onTollBoothChange(event: any, index: number, trip: 'partida' | 'retorno'): void {
        const tollBoothId = event.target.value;
        const formArray = trip === 'partida' ? this.tollPartidaExpenses : this.tollRetornoExpenses;
        if (tollBoothId) {
            this.loadSegments(parseInt(tollBoothId), index);
        } else {
            const tollGroup = formArray.at(index);
            if (tollGroup) {
                tollGroup.patchValue({ segment_id: '' });
            }
        }
    }

    // Method to get segments for a specific toll expense row
    getSegmentsForToll(tollIndex: number, trip: 'partida' | 'retorno'): Segment[] {
        const formArray = trip === 'partida' ? this.tollPartidaExpenses : this.tollRetornoExpenses;
        const tollGroup = formArray.at(tollIndex);
        const tollBoothId = tollGroup?.get('toll_booth_id')?.value;
        if (tollBoothId && this.segmentsByTollBooth[tollBoothId]) {
            return this.segmentsByTollBooth[tollBoothId];
        }
        return [];
    }

    loadSegments(tollBoothId: number, _index: number): void {
        if (this.segmentsByTollBooth[tollBoothId]) {
            return;
        }
        this.expenseService.getSegmentsByTollBooth(tollBoothId).subscribe({
            next: (segments) => {
                this.segmentsByTollBooth[tollBoothId] = segments;
            },
            error: () => {
                this.segmentsByTollBooth[tollBoothId] = [];
            }
        });
    }

    // Get segments for a specific toll booth
    getSegmentsForTollBooth(tollBoothId: number): Segment[] {
        return this.segmentsByTollBooth[tollBoothId] || [];
    }

    // Accommodation expense methods
    addAccommodationExpense(data?: any): void {
        const accommodationGroup = this.fb.group({
            id: [data?.id ?? null],
            expense_id: [data?.expense_id ?? null],
            service_id: [data?.service_id ?? null],
            amount: [data?.amount ?? ''],
            payment_method_id: [data?.payment_method_id != null ? +data.payment_method_id : ''],
            stay_duration: [data?.stay_duration ?? ''],
            evidence: [data?.evidence ?? '']
        }, { validators: this.accommodationFieldValidator() });
        this.accommodationExpenses.push(accommodationGroup);
    }

    removeAccommodationExpense(index: number): void {
        this.removeExpenseItem(this.accommodationExpenses, index, 'accommodation');
    }

    // Traffic fine expense methods
    addTrafficFineExpense(data?: any): void {
        const fineGroup = this.fb.group({
            id: [data?.id ?? null],
            expense_id: [data?.expense_id ?? null],
            service_id: [data?.service_id ?? null],
            amount: [data?.amount ?? ''],
            payment_method_id: [data?.payment_method_id != null ? +data.payment_method_id : ''],
            fine_type_id: [data?.fine_type_id != null ? +data.fine_type_id : ''],
            justification: [data?.justification ?? ''],
            evidence: [data?.evidence ?? '']
        }, { validators: this.trafficFineFieldValidator() });
        this.trafficFineExpenses.push(fineGroup);
    }

    removeTrafficFineExpense(index: number): void {
        this.removeExpenseItem(this.trafficFineExpenses, index, 'traffic_fine');
    }

    // Food expense methods
    addFoodExpense(data?: any): void {
        const foodGroup = this.fb.group({
            id: [data?.id ?? null],
            expense_id: [data?.expense_id ?? null],
            service_id: [data?.service_id ?? null],
            amount: [data?.amount ?? ''],
            description: [data?.description ?? ''],
            payment_method_id: [data?.payment_method_id != null ? +data.payment_method_id : ''],
            evidence: [data?.evidence ?? '']
        }, { validators: this.foodFieldValidator() });
        this.foodExpenses.push(foodGroup);
    }

    removeFoodExpense(index: number): void {
        this.removeExpenseItem(this.foodExpenses, index, 'food');
    }

    // Other expense methods
    addOtherExpense(data?: any): void {
        const otherGroup = this.fb.group({
            id: [data?.id ?? null],
            expense_id: [data?.expense_id ?? null],
            service_id: [data?.service_id ?? null],
            amount: [data?.amount ?? ''],
            description: [data?.description ?? ''],
            payment_method_id: [data?.payment_method_id != null ? +data.payment_method_id : ''],
            evidence: [data?.evidence ?? '']
        }, { validators: this.otherFieldValidator() });
        this.otherExpenses.push(otherGroup);
    }

    removeOtherExpense(index: number): void {
        this.removeExpenseItem(this.otherExpenses, index, 'other');
    }

    // File upload for evidence
    onFileUpload(event: any, type: string, index: number): void {
        const file = event.target.files[0];
        if (file) {
            if (type === 'general') {
                // TODO: Implement OCR processing for general file upload
            } else {
                this.setFileName(type, index, file.name);
                this.expenseService.uploadFile(file, type, index).subscribe({
                    next: (response) => {
                        const prevPath = this.uploadedPaths[type]?.[index];
                        if (prevPath) this.expenseService.deleteFile(prevPath).subscribe();
                        if (!this.uploadedPaths[type]) this.uploadedPaths[type] = {};
                        this.uploadedPaths[type][index] = response.path ?? '';
                        this.updateEvidenceField(type, index, response.url);
                    },
                    error: (error) => {
                    }
                });
            }
        }
    }

    // Update evidence field in form
    private updateEvidenceField(type: string, index: number, fileUrl: string): void {
        let formArray: FormArray;

        switch (type) {
            case 'fuel':
                formArray = this.fuelExpenses;
                break;
            case 'toll_partida':
                formArray = this.tollPartidaExpenses;
                break;
            case 'toll_retorno':
                formArray = this.tollRetornoExpenses;
                break;
            case 'accommodation':
                formArray = this.accommodationExpenses;
                break;
            case 'traffic_fine':
                formArray = this.trafficFineExpenses;
                break;
            case 'food':
                formArray = this.foodExpenses;
                break;
            case 'other':
                formArray = this.otherExpenses;
                break;
            default:
                return;
        }

        if (formArray && formArray.at(index)) {
            formArray.at(index).patchValue({ evidence: fileUrl });
        }
    }

    // Method to trigger file input click
    triggerFileInput(type: string, index: number): void {
        const fileInput = document.getElementById(`${type}-file-${index}`) as HTMLInputElement;
        if (fileInput) {
            fileInput.click();
        }
    }

    // Calculate total expenses
    calculateTotal(): number {
        return this.calculateFuelTotal()
            + this.calculateTollTotal()
            + this.calculateAccommodationTotal()
            + this.calculateTrafficFineTotal()
            + this.calculateFoodTotal()
            + this.calculateOtherTotal();
    }

    calculateFuelTotal(): number {
        let total = 0;
        this.fuelExpenses.controls.forEach(control => {
            const amount = control.get('amount')?.value;
            if (amount) total += parseFloat(amount);
        });
        return total;
    }

    calculateTollTotal(): number {
        let total = 0;
        [...this.tollPartidaExpenses.controls, ...this.tollRetornoExpenses.controls].forEach((control: any) => {
            const amount = control.get('amount')?.value;
            if (amount) total += parseFloat(amount);
        });
        return total;
    }

    calculateAccommodationTotal(): number {
        let total = 0;
        this.accommodationExpenses.controls.forEach(control => {
            const amount = control.get('amount')?.value;
            if (amount) total += parseFloat(amount);
        });
        return total;
    }

    calculateTrafficFineTotal(): number {
        let total = 0;
        this.trafficFineExpenses.controls.forEach(control => {
            const amount = control.get('amount')?.value;
            if (amount) total += parseFloat(amount);
        });
        return total;
    }

    calculateFoodTotal(): number {
        let total = 0;
        this.foodExpenses.controls.forEach(control => {
            const amount = control.get('amount')?.value;
            if (amount) total += parseFloat(amount);
        });
        return total;
    }

    calculateOtherTotal(): number {
        let total = 0;
        this.otherExpenses.controls.forEach(control => {
            const amount = control.get('amount')?.value;
            if (amount) total += parseFloat(amount);
        });
        return total;
    }

    // Count records by expense type
    countFuelRecords(): number {
        return this.fuelExpenses.controls.filter(control => 
            control.get('amount')?.value && control.get('amount')?.value !== ''
        ).length;
    }

    countTollRecords(): number {
        return [...this.tollPartidaExpenses.controls, ...this.tollRetornoExpenses.controls]
            .filter((control: any) => control.get('amount')?.value && control.get('amount')?.value !== '')
            .length;
    }

    countAccommodationRecords(): number {
        return this.accommodationExpenses.controls.filter(control => 
            control.get('amount')?.value && control.get('amount')?.value !== ''
        ).length;
    }

    countTrafficFineRecords(): number {
        return this.trafficFineExpenses.controls.filter(control => 
            control.get('amount')?.value && control.get('amount')?.value !== ''
        ).length;
    }

    countFoodRecords(): number {
        return this.foodExpenses.controls.filter(control => 
            control.get('amount')?.value && control.get('amount')?.value !== ''
        ).length;
    }

    countOtherRecords(): number {
        return this.otherExpenses.controls.filter(control => 
            control.get('amount')?.value && control.get('amount')?.value !== ''
        ).length;
    }

    // Get service status with dynamic colors
    getServiceStatus(): { name: string, bg: string, color: string } {
        if (!this.getService?.status) {
            return { name: 'Sin estado', bg: '#D8D8D8', color: '#545454' };
        }

        const status = this.getService.status;
        
        // Use colors directly from backend
        return { 
            name: status.name, 
            bg: status.bg || '#D8D8D8',
            color: status.color || '#545454'
        };
    }

    getStatusBadgeClass(): string {
        const status = this.getServiceStatus();
        return 'badge'; // Base class, styles will be applied via ngStyle
    }

    getStatusBadgeStyles(): { [key: string]: string } {
        const status = this.getServiceStatus();
        return {
            'background-color': status.bg,
            'color': status.color
        };
    }

    // Submit for Registro #1 (individual expenses when group is locked)
    onSubmitIndividual(): void {
        this.submitted = true;
        if (!this.expenseForm.valid) return;
        this.spinner.show();

        const formData = this.expenseForm.value;
        const serviceId = formData.service_id;

        const leaveFirst$ = this.sharedGroup
            ? this.expenseService.leaveSharedGroup(this.sharedGroup.group_id, serviceId).pipe(catchError(() => of(null)))
            : of(null);

        leaveFirst$.subscribe(() => {
            const expenseReport: ExpenseReport = {
                expense: {
                    service_id: serviceId,
                    status_id: formData.status_id,
                    is_removed: false
                },
                fuel_expenses: formData.fuel_expenses || [],
                toll_partida_expenses: (formData.toll_partida_expenses || []).map((t: any) => ({ ...t, trip: 'partida' })),
                toll_retorno_expenses: (formData.toll_retorno_expenses || []).map((t: any) => ({ ...t, trip: 'retorno' })),
                accommodation_expenses: formData.accommodation_expenses || [],
                traffic_fine_expenses: formData.traffic_fine_expenses || [],
                food_expenses: formData.food_expenses || [],
                other_expenses: formData.other_expenses || [],
            };

            this.expenseService.getExpenseByServiceId(serviceId).subscribe({
                next: (existingExpense) => {
                    const request$ = existingExpense?.id
                        ? this.expenseService.updateExpenseReport(existingExpense.id, expenseReport)
                        : this.expenseService.createExpenseReport(expenseReport);
                    request$.subscribe({
                        next: () => {
                            this.spinner.hide();
                            this.showSuccessToast(existingExpense?.id ? 'actualizados' : 'registrados');
                            setTimeout(() => { this.submitted = false; this.loadAllData(); }, 1500);
                        },
                        error: () => { this.spinner.hide(); this.showErrorToast(); }
                    });
                },
                error: () => {
                    this.expenseService.createExpenseReport(expenseReport).subscribe({
                        next: () => {
                            this.spinner.hide();
                            this.showSuccessToast('registrados');
                            setTimeout(() => { this.submitted = false; this.loadAllData(); }, 1500);
                        },
                        error: () => { this.spinner.hide(); this.showErrorToast(); }
                    });
                }
            });
        });
    }

    // Form submission
    onSubmit(): void {
        this.submitted = true;
        if (this.expenseForm.valid) {
            // Show loading spinner
            this.spinner.show();

            const formData = this.expenseForm.value;

            const pre = (this.sharedGroup && this.preUnificationExpense) ? this.preUnificationExpense : null;
            const merge = (preItems: any[] = [], formItems: any[] = []) =>
                [...(preItems || []), ...(formItems || [])];
            const isOtherItem = (item: any) =>
                item.expense_id && item.expense_id !== this.currentServiceExpenseId;
            const myItems = (items: any[]) => (items || []).filter((item: any) => !isOtherItem(item));

            const expenseReport: ExpenseReport = {
                expense: {
                    service_id: formData.service_id,
                    status_id: formData.status_id,
                    is_removed: false
                },
                fuel_expenses: pre
                    ? merge(pre.fuel_expenses, myItems(formData.fuel_expenses))
                    : myItems(formData.fuel_expenses),
                toll_partida_expenses: pre
                    ? merge(pre.toll_partida_expenses, myItems(formData.toll_partida_expenses)).map((t: any) => ({ ...t, trip: 'partida' }))
                    : myItems(formData.toll_partida_expenses).map((t: any) => ({ ...t, trip: 'partida' })),
                toll_retorno_expenses: pre
                    ? merge(pre.toll_retorno_expenses, myItems(formData.toll_retorno_expenses)).map((t: any) => ({ ...t, trip: 'retorno' }))
                    : myItems(formData.toll_retorno_expenses).map((t: any) => ({ ...t, trip: 'retorno' })),
                accommodation_expenses: pre
                    ? merge(pre.accommodation_expenses, myItems(formData.accommodation_expenses))
                    : myItems(formData.accommodation_expenses),
                traffic_fine_expenses: pre
                    ? merge(pre.traffic_fine_expenses, myItems(formData.traffic_fine_expenses))
                    : myItems(formData.traffic_fine_expenses),
                food_expenses: pre
                    ? merge(pre.food_expenses, myItems(formData.food_expenses))
                    : myItems(formData.food_expenses),
                other_expenses: pre
                    ? merge(pre.other_expenses, myItems(formData.other_expenses))
                    : myItems(formData.other_expenses),
            };

            // Collect items belonging to other services in the group that need to be updated
            const sharedItems: Array<{ type: string; id: number; [key: string]: any }> = [];
            const typeMap: Array<{ key: string; type: string }> = [
                { key: 'fuel_expenses',          type: 'fuel' },
                { key: 'toll_partida_expenses',  type: 'toll_partida' },
                { key: 'toll_retorno_expenses',  type: 'toll_retorno' },
                { key: 'accommodation_expenses', type: 'accommodation' },
                { key: 'traffic_fine_expenses',  type: 'traffic_fine' },
                { key: 'food_expenses',          type: 'food' },
                { key: 'other_expenses',         type: 'other' },
            ];
            typeMap.forEach(({ key, type }) => {
                (formData[key] || []).filter((item: any) => isOtherItem(item) && item.id)
                    .forEach((item: any) => sharedItems.push({ ...item, type }));
            });

            // Check if we're updating an existing expense or creating a new one
            const serviceId = formData.service_id;
            let request$;

            // First check if expense exists for this service
            this.expenseService.getExpenseByServiceId(serviceId).subscribe({
                next: (existingExpense) => {
                    if (existingExpense?.id) {
                        request$ = this.expenseService.updateExpenseReport(existingExpense.id, expenseReport);
                    } else {
                        request$ = this.expenseService.createExpenseReport(expenseReport);
                    }

                    const saves$ = sharedItems.length
                        ? forkJoin([request$, this.expenseService.bulkUpdateSharedItems(sharedItems)])
                        : request$;

                    saves$.subscribe({
                        next: () => {
                            this.spinner.hide();
                            this.showSuccessToast(existingExpense?.id ? 'actualizados' : 'registrados');
                            setTimeout(() => { this.submitted = false; this.loadAllData(); }, 1500);
                        },
                        error: () => {
                            this.spinner.hide();
                            this.showErrorToast();
                        }
                    });
                },
                error: () => {
                    // Default to creating new expense if check fails
                    const create$ = this.expenseService.createExpenseReport(expenseReport);
                    const saves$ = sharedItems.length
                        ? forkJoin([create$, this.expenseService.bulkUpdateSharedItems(sharedItems)])
                        : create$;
                    saves$.subscribe({
                        next: () => {
                            this.spinner.hide();
                            this.showSuccessToast('registrados');
                            setTimeout(() => { this.submitted = false; this.loadAllData(); }, 1500);
                        },
                        error: () => {
                            this.spinner.hide();
                            this.showErrorToast();
                        }
                    });
                }
            });
        } else {
            // Mark form fields as touched to show validation errors
            this.expenseForm.markAllAsTouched();
        }
    }

    // Show success toast notification
    private showSuccessToast(action: string): void {
        
        const notification = {
            id: 'expense-success-' + Date.now(),
            title: '✅ Viáticos ' + action,
            message: `Los gastos de viáticos han sido ${action} exitosamente.`,
            type: 'success' as const,
            timestamp: new Date(),
            data: null
        };
        // Add notification to the notification service
        this.notificationService.addNotification(notification);
        
    }

    // Show error toast notification
    private showErrorToast(): void {
        
        const notification = {
            id: 'expense-error-' + Date.now(),
            title: '❌ Error',
            message: 'No se pudieron guardar los gastos de viáticos. Por favor, intente nuevamente.',
            type: 'error' as const,
            timestamp: new Date(),
            data: null
        };
        
        // Add notification to the notification service
        this.notificationService.addNotification(notification);
    }

    onCancel(): void {
        this.router.navigate(
            [this.fromServices ? '/service/list' : '/service/deposits'],
            this.returnTab ? { queryParams: { tab: this.returnTab } } : {}
        );
        // if (this.sharedGroup && !this.formSaved) {
        //     this.expenseService.dissolveSharedGroup(this.sharedGroup.group_id).subscribe({
        //         complete: () => this.router.navigate(
        //                         [this.fromServices ? '/service/list' : '/service/deposits'],
        //                         this.fromServices && this.returnTab ? { queryParams: { tab: this.returnTab } } : {}
        //                     ),
        //         error:    () => this.router.navigate(
        //                         [this.fromServices ? '/service/list' : '/service/deposits'],
        //                         this.fromServices && this.returnTab ? { queryParams: { tab: this.returnTab } } : {}
        //                     ),
        //     });
        // } else {
        //     this.router.navigate(
        //                         [this.fromServices ? '/service/list' : '/service/deposits'],
        //                         this.fromServices && this.returnTab ? { queryParams: { tab: this.returnTab } } : {}
        //                     );
        // }
    }

    @HostListener('window:beforeunload')
    onBeforeUnload(): void {
        this.deleteUploadedFilesIfUnsaved();
    }

    ngOnDestroy(): void {
        this.deleteUploadedFilesIfUnsaved();
    }

    private deleteUploadedFilesIfUnsaved(): void {
        if (this.formSaved) return;
        for (const type of Object.keys(this.uploadedPaths)) {
            for (const index of Object.keys(this.uploadedPaths[type])) {
                const path = this.uploadedPaths[type][+index];
                if (path) this.expenseService.deleteFile(path).subscribe();
            }
        }
    }

    toggleSummary(): void {
        this.showSummary = !this.showSummary;
    }

    toggleSection(section: string): void {
        this.sectionCollapsed[section] = !this.sectionCollapsed[section];
    }

    onFileChangeOCR(event: any) {
        const file = event.target.files[0];
        this.expenseService.readImage(file).subscribe(res=>{
        });
    }

}
